import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { repairerInvoiceApi, RepairerInvoiceListItem } from '../../api/repairerInvoiceApi';
import { formatPrice } from '@/utils/priceHelpers';

interface Props {
  invoice: RepairerInvoiceListItem;
  onClose: () => void;
  onPaid: () => void;
}

/**
 * Modal "J'ai payé" — déclaration de paiement par le vendeur.
 *
 * Affiche IBAN/BIC/titulaire en clair (le vendeur est authentifié), avec
 * bouton "Copier l'IBAN" pour faciliter le virement. Demande date du
 * virement (défaut = aujourd'hui) + référence optionnelle.
 */
export default function MarkPaidDialog({ invoice, onClose, onPaid }: Props) {
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await repairerInvoiceApi.markPaid(invoice.id, paymentDate, reference.trim() || undefined);
      toast.success('Paiement enregistré, le réparateur sera notifié');
      onPaid();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
      setSubmitting(false);
    }
  };

  const copyIban = () => {
    if (invoice.iban) {
      navigator.clipboard.writeText(invoice.iban);
      toast.success('IBAN copié');
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-max-w-lg tw-w-full">
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b">
          <h2 className="tw-text-lg tw-font-semibold">Déclarer le paiement</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="tw-p-6 tw-space-y-4">
          <div className="tw-bg-gray-50 tw-rounded tw-p-3 tw-text-sm tw-space-y-1">
            <p><strong>SAV #{invoice.savId}</strong> — facture {invoice.invoiceNumber}</p>
            <p>Montant à régler : <strong>{formatPrice(invoice.amountTtc)}</strong></p>
            {invoice.iban && (
              <>
                <p className="tw-flex tw-items-center tw-gap-2">
                  IBAN : <span className="tw-font-mono">{invoice.iban}</span>
                  <button type="button" onClick={copyIban} className="tw-text-affinicar-primary tw-underline tw-text-xs">
                    Copier
                  </button>
                </p>
                <p>BIC : <span className="tw-font-mono">{invoice.bic}</span></p>
                <p>Titulaire : {invoice.accountHolder}</p>
              </>
            )}
            {invoice.paymentReferenceSuffix && (
              <p>Référence à mettre sur le virement : <span className="tw-font-mono">SAV-{invoice.savId}-{invoice.paymentReferenceSuffix}</span></p>
            )}
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-1">Date du virement</label>
            <input
              type="date"
              required
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded"
            />
          </div>
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-1">Référence du virement (optionnel)</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ex: VIR-12345"
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded"
            />
          </div>

          <div className="tw-flex tw-gap-2 tw-justify-end tw-pt-3 tw-border-t">
            <button type="button" onClick={onClose} className="tw-px-4 tw-py-2 tw-text-gray-700">Annuler</button>
            <button
              type="submit"
              disabled={submitting}
              className="tw-bg-affinicar-primary tw-text-white tw-px-4 tw-py-2 tw-rounded tw-font-medium tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50"
            >
              {submitting && <Loader2 className="tw-animate-spin" size={16} />}
              Confirmer le paiement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
